import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { isSessionError, verifyMfa } from "../api-service/auth";
import { enableFallback } from "../api-service/secret-questions";
import { ROUTES, clearUserInfo, getOrgId } from "../common";
import messages from "../messages/ja.json";
import { CapturedImage, FaceCapture } from "../parts/face-capture";
import { Icon } from "../parts/icon";
import { RedirectOverlay } from "../parts/redirect-overlay";
import { useExternalRedirect } from "../parts/use-external-redirect";
import { VerifyMfaShell } from "../parts/verify-mfa-shell";
import { useAuth } from "../provider";

import "../styles/auth-flow.css";

// enable-fallback reasons: the camera can't be opened, vs a face match too low to pass.
const REASON_CAMERA = "camera_unavailable";
const REASON_LOW_MATCH = "low_similarity";

// How many failed face attempts unlock the secret-question link: it appears once this many
// have failed, so the user retries a few times first — a couple of misses are usually
// lighting/angle. Reaching it also ends the auto-capture loop, because at that point
// re-running verify on the same face only burns requests.
const FALLBACK_AFTER_FAILURES = 3;

export function VerifyMfaPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ similarity?: string } | null>(null);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  // The localized reason the camera could not be opened (empty = no camera failure). It is
  // kept rather than reduced to a boolean because FaceCapture no longer shows it in the
  // エラー modal here — this screen renders it inline, above the secret-question link.
  const [cameraErrorMessage, setCameraErrorMessage] = useState("");
  // Set on any failed match (no similarity threshold for now); offers the fallback link.
  // Sticky: never cleared once set, so the link stays put across further retries.
  const [lowMatch, setLowMatch] = useState(false);
  // How many face attempts have failed in this session. Drives FALLBACK_AFTER_FAILURES.
  const [failCount, setFailCount] = useState(0);
  // The pending challenge is gone (expired / invalid session): terminal, no retry can fix it.
  const [sessionExpired, setSessionExpired] = useState(false);
  // The backend's fallback message shown alongside the secret-question link.
  const [fallbackMessage, setFallbackMessage] = useState("");
  // Whether enable-fallback has already succeeded (so the link click doesn't call it again).
  const [fallbackEnabled, setFallbackEnabled] = useState(false);
  const [enabling, setEnabling] = useState(false);

  // Hand-off to Salesforce. While it runs the whole screen is covered (no capture, no
  // fallback link, no cancel); if it fails the screen unlocks so the user can scan again
  // or switch to the secret question.
  const redirect = useExternalRedirect((msg) => {
    setDone(false);
    setError(msg);
    toast.error(msg);
  });

  // Prefer the signed-in user's org_id; fall back to the persisted Salesforce org_id.
  const orgId = auth.user?.orgId || getOrgId();
  // The secret-question fallback needs enrolled questions.
  const secretRegistered = auth.secretStatus === "registered";
  // Camera-failure link: only if the camera errored AND secret questions are registered.
  const cameraFallbackAvailable = Boolean(cameraErrorMessage) && secretRegistered;
  // Out of retries: the auto-capture loop stops here whether or not the secret question is
  // available, so a user without enrolled questions can't keep the camera firing forever.
  const retriesExhausted = failCount >= FALLBACK_AFTER_FAILURES;
  // Failed-match link: appears once the retries are used up, and stays from then on
  // (failCount only grows) — they should never have to fail another FALLBACK_AFTER_FAILURES
  // times to get it back.
  // A dead session is excluded: the fallback runs on that same session, so it can't help.
  const lowMatchFallbackAvailable = lowMatch && retriesExhausted && !sessionExpired;

  const onVerifyMfa = async (image: CapturedImage) => {
    if (!orgId || done) return;
    setSubmitting(true);
    setResult(null);
    setError("");
    // lowMatch is NOT cleared here: once the link has been unlocked it must survive every
    // further retry, so the user can switch to the secret question at any point.
    try {
      const res = await verifyMfa(image.blob);
      // Success: the backend authenticated the face → redirect back to Salesforce.
      if (res.status === "authenticated" || res.redirectUrl) {
        clearUserInfo();
        setDone(true);
        setSubmitting(false); // hide the "認証中" overlay so the success toast is the focus
        toast.success(messages.checkin.loginSuccess, { icon: <Icon name="tick" size={27} /> });
        if (res.redirectUrl) {
          redirect.start(res.redirectUrl);
          return;
        }
        setResult({});
        return;
      }
      // Near-miss: the backend already marked the pending as fallback-eligible → just offer
      // the secret-question link (no enable-fallback round-trip needed here).
      if (res.status === "fallback_available") {
        const failures = failCount + 1;
        setFailCount(failures);
        if (secretRegistered) {
          setFallbackEnabled(true);
          setFallbackMessage(res.message || messages.checkin.fallbackPrompt);
          setLowMatch(true);
          // Still below the retry threshold: the link stays hidden, so surface the
          // failure itself — otherwise the screen would show nothing at all.
          if (failures < FALLBACK_AFTER_FAILURES) {
            setError(res.message || messages.checkin.verifyFailed);
          }
          setSubmitting(false);
          return;
        }
        // Fallback offered but the user has no enrolled questions → failed attempt.
        setError(res.message || messages.checkin.verifyFailed);
        setSubmitting(false);
        return;
      }
      // Any other status → failed attempt, allow a retake. No similarity threshold for now:
      // every failed match offers the secret question too (BE flag not required), but only
      // after FALLBACK_AFTER_FAILURES tries. Unlike the near-miss path we leave
      // fallbackEnabled false, so the link calls enable-fallback first.
      setError(res.message || messages.checkin.verifyFailed);
      setFailCount(failCount + 1);
      if (secretRegistered) setLowMatch(true);
      setSubmitting(false);
    } catch (err: any) {
      // A plain mismatch comes back as a rejection (only near_miss is mapped to a status), so
      // this branch must offer the secret question too — same no-threshold rule as above.
      const msg = err?.error?.message || err?.message || messages.checkin.error;
      setError(msg);
      // Dead session: stop everything now rather than burning the retry budget on attempts
      // that can only fail the same way. The user re-enters through login (cancel button).
      if (isSessionError(err)) {
        setSessionExpired(true);
        setSubmitting(false);
        return;
      }
      setFailCount(failCount + 1);
      if (secretRegistered) setLowMatch(true);
      setSubmitting(false);
    }
  };

  // Switch to the secret-question fallback (enabling it first if not already), then go to
  // the answer page. Shared by the camera-failure and low-match links.
  const goToSecretQuestion = async (reason: string) => {
    setEnabling(true);
    setError("");
    try {
      if (!fallbackEnabled) await enableFallback(reason);
      navigate(ROUTES.secretQuestionsVerify, { replace: true });
    } catch (err: any) {
      const msg =
        err?.error?.message || err?.message || messages.secretQuestions.fallbackEnableError;
      setError(msg);
      toast.error(msg);
      setEnabling(false);
    }
  };

  const footer = (
    <button
      type="button"
      className="verify-mfa-cancel"
      onClick={() => navigate(ROUTES.login, { replace: true })}
      disabled={submitting || enabling || done}
    >
      {messages.face.cancel}
    </button>
  );

  return (
    <VerifyMfaShell username={auth.user?.username} footer={footer}>
      <FaceCapture
        onCapture={onVerifyMfa}
        onCameraError={setCameraErrorMessage}
        // Show the failure inline next to the secret-question link instead of in the エラー
        // modal: here a dead camera has a way forward, and a modal would cover it.
        suppressCameraErrorDialog
        // Stop auto-capturing once the retries are used up (or the session died): the face
        // clearly isn't matching, so re-running verify on a loop only burns requests. The
        // user switches to the secret question, or cancels back to login, from here.
        disabled={!orgId || done || retriesExhausted || sessionExpired}
        submitting={submitting}
        mode="checkin"
      />

      {result ? (
        <p className="success-banner verify-mfa-message">
          {messages.checkin.matched}
          {result.similarity ? ` (${result.similarity}%)` : ""}
        </p>
      ) : null}

      {!orgId ? (
        <p className="error-banner verify-mfa-message">{messages.checkin.error}</p>
      ) : null}

      {error ? <p className="error-banner verify-mfa-message">{error}</p> : null}

      {/* Why the camera failed. Rendered even when the fallback link is not available (no
          enrolled questions), so a camera failure is never silent now that the modal is off.
          .error-banner is white-space: pre-line, which keeps face.cameraError's two lines. */}
      {cameraErrorMessage && !done ? (
        <p className="error-banner verify-mfa-message">{cameraErrorMessage}</p>
      ) : null}

      {/* FE-2 (camera): after a camera error, if secret questions are registered. */}
      {cameraFallbackAvailable && !done ? (
        <div className="verify-mfa-message">
          <p className="loading-copy">{messages.secretQuestions.fallbackHelp}</p>
          <button
            type="button"
            className="link-button"
            onClick={() => goToSecretQuestion(REASON_CAMERA)}
            disabled={enabling}
          >
            {messages.secretQuestions.fallbackLink}
          </button>
        </div>
      ) : null}

      {/* FE-2 (failed match): offered for any similarity below the pass mark, not just the
          backend's near-miss flag, and only after FALLBACK_AFTER_FAILURES failed attempts. */}
      {lowMatchFallbackAvailable && !done ? (
        <div className="verify-mfa-message">
          <p className="loading-copy">{fallbackMessage || messages.checkin.fallbackPrompt}</p>
          <button
            type="button"
            className="link-button"
            onClick={() => goToSecretQuestion(REASON_LOW_MATCH)}
            disabled={enabling}
          >
            {messages.secretQuestions.fallbackLink}
          </button>
        </div>
      ) : null}

      {/* Hand-off in flight: block every interaction underneath until it completes or
          the watchdog reports a failure. */}
      {redirect.redirecting ? <RedirectOverlay label={messages.checkin.redirecting} /> : null}
    </VerifyMfaShell>
  );
}
